/* eslint-disable no-param-reassign */
import {
  CloseCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  Table as AntTable,
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import { getCatalogoDePadrones } from 'api/catalogos';
import { patchCiudadano } from 'api/cuentaunicasir/ciudadanos';
import { getTiposDeAplicacion, postGenerarDescuentosEspeciales } from 'api/recaudacion/index';
import { getTipoDeDescuento } from 'api/recaudacion/tiposDeDescuento';
import checkToken from 'api/utils/checkToken';
import ModalAgregarDescuentos, { columns as columnsCargos } from 'components/Catalogos/ModalAgregarDescuentos';
import ModalAutorizarDescuentosEspeciales from 'components/Catalogos/ModalAutorizarDescuentosEspeciales';
import ModalCancelarDescuentosAplicados from 'components/Catalogos/ModalCancelarDescuentosAplicados';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import InputSearch from 'components/InputSearch';
import LadaNumero from 'components/LadaNumero';
import InfoPadron, { avilablePadrones } from 'components/PadronesInfo';
import Select from 'components/Select';
import Table from 'components/Table';
import useAdeudoPadron from 'hooks/useAdeudoPadron';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import address from 'utils/address';
import { recalculateCargos } from 'utils/cargos';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';
import { listToString } from 'utils/normalizers';
import { getHelpTexts, gettersPadronesCajaMap } from 'utils/padrones';
import padronToString, { getPadronKey } from 'utils/padronesToString';
import { ActionsNames, hasActionPermission, isAdmin } from 'utils/permission/index';
import toCurrency from 'utils/toCurrency';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import ModalAlertas from 'views/Recaudacion/Caja/ModalAlertas';
import { padronesEnum } from 'views/Tramites/GPM/enums';

const { Title, Text } = Typography;

const cleanCargos = (e) => e.estados_globales !== 5 && !e?.tipo_de_cargo?.no_aplica_descuento;
const DEV = process.env.NODE_ENV === 'development';

const ESTADOS_DEL_DESCUENTO = Object.freeze({
  VIGENTE: 1,
  APLICADO: 2,
  CANCELADO: 3,
  VENCIDO: 4,
});

const filterDescuentosAplicados = (d) => d
  .estado_del_descuento === ESTADOS_DEL_DESCUENTO.APLICADO
  || d.estados_globales === ESTADOS_GLOBALES_MAP.AUTORIZADO;

const filterDescuentosNoAplicados = (d) => d
  .estado_del_descuento !== ESTADOS_DEL_DESCUENTO.APLICADO
  && d.estados_globales !== ESTADOS_GLOBALES_MAP.AUTORIZADO;

// Funciones auxiliares para procesar descuentos
const procesarDescuentosAplicables = (descuentos_aplicables, cargo) => descuentos_aplicables
  .filter(filterDescuentosAplicados)
  .map((d) => ({ ...d, cargo }));

const procesarDescuentosPorTipo = (items, tipoRelacion) => items
  .map((item) => item.descuentos.map((d) => ({ ...d, [tipoRelacion]: item })))
  .flat()
  .filter(filterDescuentosAplicados);

const procesarDescuentosNoAplicadosPorTipo = (items, tipoRelacion) => items
  .map((item) => item.descuentos.map((d) => ({ ...d, [tipoRelacion]: item })))
  .flat()
  .filter(filterDescuentosNoAplicados);

const calcularTotalDescuentos = (descuentos) => descuentos
  .map((d) => d.importe_total)
  .reduce((accum, curr) => accum + (curr * 1000), 0) / 1000 || 0;

const procesarDescuentosDeCargo = (item) => {
  const {
    descuentos_aplicables,
    descuentos_especiales,
    actualizaciones,
    recargosMulta,
    recargos,
    gastos,
    tipo_de_cargo,
  } = item;

  const periodo = tipo_de_cargo?.periodo_fiscal?.periodo;

  // Procesar descuentos aplicados
  const descuentosAplicablesTotal = procesarDescuentosAplicables(descuentos_aplicables, item);
  const descuentosAplicadosRecargos = procesarDescuentosPorTipo(recargos, 'recargo');
  const descuentosAplicadosActualizaciones = procesarDescuentosPorTipo(actualizaciones, 'actualizacion');
  const descuentosAplicadosGastos = procesarDescuentosPorTipo(gastos, 'gasto');
  const descuentosAplicadosMultas = procesarDescuentosPorTipo(recargosMulta, 'recargo');

  // Procesar descuentos no aplicados
  const descuentosNoAplicadosRecargos = procesarDescuentosNoAplicadosPorTipo(recargos, 'recargo');
  const descuentosNoAplicadosActualizaciones = procesarDescuentosNoAplicadosPorTipo(actualizaciones, 'actualizacion');
  const descuentosNoAplicadosGastos = procesarDescuentosNoAplicadosPorTipo(gastos, 'gasto');

  // procesar descuentos especiales
  const descuentosEspecialesAplicados = descuentos_especiales
    .filter(filterDescuentosAplicados)
    .map((d) => ({ ...d, cargo: item, es_especial: true }));

  const descuentosEspecialesNoAplicados = descuentos_especiales
    .filter(filterDescuentosNoAplicados)
    .map((d) => ({ ...d, cargo: item, es_especial: true }));

  // Consolidar arrays
  const descuentosAplicadosArr = [
    ...descuentosEspecialesAplicados,
    ...descuentosAplicablesTotal,
    ...descuentosAplicadosRecargos,
    ...descuentosAplicadosMultas,
    ...descuentosAplicadosGastos,
    ...descuentosAplicadosActualizaciones,
  ].map((d) => ({ ...d, periodo }));

  const descuentosNoAplicadosArr = [
    ...descuentosEspecialesNoAplicados,
    ...descuentosNoAplicadosRecargos,
    ...descuentosNoAplicadosActualizaciones,
    ...descuentosNoAplicadosGastos,
  ].map((d) => ({ ...d, periodo }));

  // Calcular totales
  const descuento_total_aplicado = calcularTotalDescuentos([
    ...descuentos_especiales.filter(filterDescuentosAplicados),
    ...descuentosAplicadosArr,
  ]);

  const descuentos_por_autorizar = calcularTotalDescuentos([
    ...descuentos_especiales.filter(filterDescuentosNoAplicados),
    ...descuentosNoAplicadosArr,
  ]);

  return {
    descuentosAplicadosArr,
    descuentosNoAplicadosArr,
    descuento_total_aplicado,
    descuentos_por_autorizar,
  };
};

const crearCargoNormalizado = (item, cargos, descuentosProcesados) => {
  const cargoPadre = cargos.find((e) => e.id === item?.cargo_padre) || item?.cargo_padre;

  return {
    ...item,
    ...descuentosProcesados,
    cargoPadre,
    key: item.id,
    periodo: item?.tipo_de_cargo?.periodo_fiscal?.periodo,
    actualizacion_total: item?.actualizaciones_base,
    recargo_total: item?.recargos_base,
    importe_total: item?.adeudo_total,
    gasto_total: item?.gastos_totales,
  };
};

const obtenerAccesorios = (cargos, cargoPadreId) => cargos
  .filter((cargo) => cargo.cargo_padre === cargoPadreId && cargo.es_accesorio);

const crearEtiquetasAdicionales = (accesorios) => accesorios.map((accesorio) => (
  <li key={accesorio.id}>
    {accesorio.descripcion}
    (
    {toCurrency(accesorio.importe)}
    )
    <br />
    {accesorio.descuentos_aplicables?.length ? 'Descuentos:' : ''}
    <ul>
      {accesorio.descuentos_aplicables.map((descuento) => (
        <li key={descuento.id}>
          {descuento.comentarios}
          {' '}
          (
          {toCurrency(descuento.importe_total)}
          )
        </li>
      ))}
    </ul>
  </li>
));

const procesarCargosPrincipales = (cargos) => {
  const cargosPrincipales = cargos.filter((cargo) => !cargo.cargo_padre
    || !cargo.es_accesorio
    || cargos.some((otro) => otro.cargo_padre === cargo.id || otro.id === cargo.cargo_padre));

  return cargosPrincipales.reduce((acumulador, cargoPrincipal) => {
    const accesorios = obtenerAccesorios(cargos, cargoPrincipal.id);

    // Calcular total de adicionales
    cargoPrincipal.adicionales = accesorios
      .reduce((total, accesorio) => total + accesorio.importe_total, 0);

    // Crear etiquetas de adicionales
    cargoPrincipal.adicionalesLabel = crearEtiquetasAdicionales(accesorios);

    return acumulador.concat([...accesorios, cargoPrincipal]);
  }, []).reverse();
};

const normalizeCargos = (cargos) => {
  const cargosCalculados = recalculateCargos(cargos, []);

  const cargosNormalizados = cargosCalculados
    .filter(cleanCargos)
    .map((item) => {
      const descuentosProcesados = procesarDescuentosDeCargo(item);
      return crearCargoNormalizado(item, cargos, descuentosProcesados);
    });

  return procesarCargosPrincipales(cargosNormalizados);
};

// eslint-disable-next-line react/prop-types
const DescuentosEspeciales = ({ permission }) => {
  const { padron_principal } = useSelector(({ auth }) => auth.entidad);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(padron_principal);
  const [padron, setPadron] = useState();
  const [descuentosDisponibles, setDescuentosDisponibles] = useState([]);
  const padrones = useSelector(({ catalogs }) => catalogs.padrones)?.filter((e) => e.id !== 5);
  const [form] = Form.useForm();
  const [_loading, setLoading] = useState(false);
  const onSuccessCallback = async (_cargos) => {
    setLoading(true);
    const desctuentosEnActualizaciones = _cargos
      .map((e) => e.actualizaciones.flatMap((a) => a.tipos_de_descuento_ids))
      .reduce((accum, curr) => accum.concat(curr), []);

    const _descuentosPorRecargo = _cargos.map((e) => e.recargos
      .map((r) => r.tipo_de_recargo.tipos_de_descuento.map((d) => d.id)))
      .reduce((accum, curr) => accum.concat(curr), []);
    const _descuentosPorMulta = _cargos.map((e) => e.recargosMulta
      .map((r) => r.tipo_de_recargo.tipos_de_descuento.map((d) => d.id)))
      .reduce((accum, curr) => accum.concat(curr), []);

    const idsDescRecargos = [...new Set([..._descuentosPorRecargo, ..._descuentosPorMulta])];
    const _descuentosPorCargo = _cargos.map((e) => e.tipo_de_cargo.tipos_de_descuento
      .map((d) => d.id))
      .reduce((accum, curr) => accum.concat(curr), []);

    const idsDescCargos = [...new Set(_descuentosPorCargo)];
    const mergedArray = [
      ...idsDescRecargos,
      ...idsDescCargos,
      ...desctuentosEnActualizaciones,
    ]
      .reduce((accum, curr) => accum.concat(curr), []);

    const uniqueArray = [...new Set(mergedArray)].sort((a, b) => a - b);
    const promises = uniqueArray.map((e) => getTipoDeDescuento(e));
    const _descuentosDisponibles = (await Promise.all(promises))
      .filter((d) => (d && d.puede_aplicar && d.es_descuento_especial));

    setDescuentosDisponibles(_descuentosDisponibles);
    setLoading(false);
  };
  const [
    loadingAdeudo,
    loadingMessage,
    cargos,
    getCargosPadron,
  ] = useAdeudoPadron(tipoDePadronSelected, padron?.id, normalizeCargos, onSuccessCallback);
  const tiposDeAplicacion = useSelector(({ catalogs }) => catalogs.tiposDeAplicacion)
    .map((e) => ({
      ...e,
      disabled: /variable|capturable/.test(e.descripcion.toLowerCase()),
    }));
  const _entidad = useSelector(({ auth }) => auth.entidad);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const [formDescuento] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [dataExterno, setDataExterno] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [visibleDescuentos, setVisibleDescuentos] = useState(false);
  const [visibleAutorizarDescuentos, setVisibleAutorizarDescuentos] = useState(false);
  const [descuentosPorAutorizar, setDescuentosPorAutorizar] = useState([]);
  const [descuentosAplicados, setDescuentosAplicados] = useState([]);
  const [visibleCancelarDescuentos, setVisibleCancelarDescuentos] = useState(false);
  const [restrict, setRestrict] = useState(false);
  const loading = _loading || loadingAdeudo;

  useEffect(() => {
    checkToken();
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = hasActionPermission('padron', ActionsNames.view);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) setHasAllViewPermission(_hasAllViewPermission);
    };
    const fetchAll = async () => {
      setLoading(true);
      await getCatalogoDePadrones();
      await getTiposDeAplicacion(true);
      if (mounted) {
        setLoading(false);
      }
    };
    fetchAll();
    validatePermission();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
      render: (text) => text,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 500,
      render: (text) => text,
    },
    {
      titleText: 'Tipo de Aplicación',
      dataIndex: 'tipo_de_aplicacion',
      key: 'tipo_de_aplicacion',
      width: 200,
      render: (val) => (tiposDeAplicacion.find((item) => item.id === val)?.descripcion),
    },
    {
      titleText: 'Rango de Fechas',
      dataIndex: 'el_descuento_es_por_rango_de_fechas',
      key: 'el_descuento_es_por_rango_de_fechas',
      width: 250,
      render: (val, record) => (val && record?.fecha_inicio && record?.fecha_fin
        ? `${record?.fecha_inicio.format('DD/MM/YYYY')} - ${record?.fecha_fin.format('DD/MM/YYYY')}`
        : 'N/A'),
    },
  ];

  const normalizePadron = (_padron) => {
    _padron._nombre_completo = padronToString(_padron, tipoDePadronSelected);
    if (tipoDePadronSelected === padronesEnum.PREDIO) {
      _padron._nombre_completo = getPadronKey(_padron, padronesEnum.PREDIO);
      _padron.direccion = typeof _padron.direccion === 'string'
        ? _padron.direccion : address(_padron.direccion);
      _padron._propietario = _padron?.nombres_de_participes?.length
        ? listToString(_padron.nombres_de_participes)
        : padronToString(_padron?.ciudadano, 1);
    }
    _padron.tipo_de_padron = tipoDePadronSelected;
    form.setFieldsValue(_padron);
    return _padron;
  };

  const handleSearchPadron = async () => {
    try {
      setLoading(true);
      const { q } = form.getFieldsValue();
      const dataPadron = await gettersPadronesCajaMap[tipoDePadronSelected]({ q });
      if ([0, 1].includes(dataPadron?.results?.length) || [1, 0].includes(dataPadron?.length)) {
        const found = dataPadron?.results?.[0] || dataPadron?.[0];
        if (found) {
          setPadron(normalizePadron(found));
        } else {
          message.warn('Padrón no encontrado', 3.5);
          setPadron();
        }
      } else {
        setDataExterno(dataPadron);
        setVisibleBusquedaPadrones(true);
        setPadron();
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const onFinish = async () => {
    try {
      await form.validateFields();
      handleSearchPadron();
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    fecha_inicial: [
      {
        validator: async (r, fecha_inicial) => {
          const { fecha_final } = formDescuento.getFieldsValue();
          if (fecha_final && (fecha_inicial?.isAfter(fecha_final)
            || fecha_final.format('DD/MM/YYYY') === fecha_inicial.format('DD/MM/YYYY'))) {
            throw new Error('El campo debe ser menor a Fecha Final');
          }
        },
      },
    ],
    fecha_final: [
      {
        validator: async (r, fecha_final) => {
          const { fecha_inicial } = formDescuento.getFieldsValue();
          if (fecha_inicial && (fecha_final?.isBefore(fecha_inicial)
            || fecha_final.format('DD/MM/YYYY') === fecha_inicial.format('DD/MM/YYYY'))) {
            throw new Error('El campo debe ser mayor a Fecha Inicial');
          }
        },
      },
    ],
  };

  const handleOnRowClick = (record) => {
    if (restrict) {
      message.warn('Este padrón tiene alertas restrictivas, no es posible aplicar descuentos.');
      return;
    }
    setSelectedRowKeys([record.id]);
  };

  const onClear = () => {
    setPadron();
    setDescuentosDisponibles([]);
    setDataExterno();
    setTipoDePadronSelected(padron_principal);
    form.resetFields();
    setSelectedRowKeys([]);
    setRestrict(false);
  };

  const onCancelDescuento = () => {
    setVisibleDescuentos(false);
    setSelectedRowKeys([]);
  };

  const FormBusqueda = () => (
    <Form
      layout="vertical"
      form={form}
      onFinish={() => onFinish()}
      scrollToFirstError
      initialValues={{ padron: padron_principal }}
      className={padron ? 'without-feedback' : ''}
    >
      <Row gutter={10}>
        {!padron && (
          <>
            <Col
              xs={24}
              sm={24}
              md={8}
            >
              <Form.Item
                name="padron"
                label="Padrón"
                hasFeedback
                rules={rules.required}
              >
                <Select
                  onChange={setTipoDePadronSelected}
                  dataSource={padrones.filter((e) => e.id !== 5)}
                />
              </Form.Item>
            </Col>
            {tipoDePadronSelected && (
              <Col
                xs={24}
                sm={24}
                md={16}
              >
                <InputSearch
                  disabled={false}
                  label={padrones
                    .find((e) => e.id === tipoDePadronSelected)?.descripcion}
                  rules={rules.required}
                  tipoDePadron={tipoDePadronSelected}
                  visibleBusquedaPadrones={visibleBusquedaPadrones}
                  setVisibleBusquedaPadrones={setVisibleBusquedaPadrones}
                  setDataExterno={setDataExterno}
                  dataExterno={dataExterno}
                  onChange={({ target: { value } }) => form.setFieldsValue({ q: value })}
                  onPressEnter={() => onFinish()}
                  title={getHelpTexts(tipoDePadronSelected)}
                  onClickAdvanced={() => setVisibleBusquedaPadrones(true)}
                />
              </Col>
            )}
          </>
        )}
        {padron && !avilablePadrones.includes(tipoDePadronSelected) && (
          <>
            {([padronesEnum.CIUDADANO, padronesEnum.CONTRIBUYENTE]
              .includes(tipoDePadronSelected)) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Clave"
                  name={tipoDePadronSelected === 1 ? 'clave_ciudadana' : 'clave'}
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {tipoDePadronSelected !== padronesEnum.MOTOCICLETA && (
              <Col xs={24} sm={24} md={tipoDePadronSelected === padronesEnum.PREDIO ? 12 : 16}>
                <Form.Item
                  label={tipoDePadronSelected !== 4 ? 'Nombre' : 'Placas/Serie'}
                  name="_nombre_completo"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {![
              padronesEnum.MOTOCICLETA,
              padronesEnum.VEHICULO,
              padronesEnum.PREDIO,
            ].includes(tipoDePadronSelected) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="RFC"
                  name="RFC"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {([padronesEnum.CIUDADANO, padronesEnum.CONTRIBUYENTE]
              .includes(tipoDePadronSelected)) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="CURP"
                  name="CURP"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {(tipoDePadronSelected === padronesEnum.PREDIO) && (
            <>
              <Col
                xs={24}
                sm={24}
                md={!padron?.ciudadano
                && padron.nombres_de_participes?.length === 1 ? 8 : 12}
              >
                <Form.Item
                  label={`Propietario${padron.nombres_de_participes?.length > 1 ? 's' : ''}`}
                  name="_propietario"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label="Dirección"
                  name="direccion"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            </>
            )}
            {![
              padronesEnum.MOTOCICLETA,
              padronesEnum.VEHICULO,
              padronesEnum.PREDIO,
            ].includes(tipoDePadronSelected) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Correo"
                  rules={rules.email}
                  name="email"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {![
              padronesEnum.MOTOCICLETA,
              padronesEnum.VEHICULO,
            ].includes(tipoDePadronSelected) && ( // 16,4
              <LadaNumero
                rules={rules}
                form={form}
                names={{
                  lada: 'lada',
                  numero_de_telefono: 'numero_de_celular',
                }}
                disabled
              />
            )}
            {tipoDePadronSelected === padronesEnum.VEHICULO && (
              <>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clase_del_vehiculo"
                    label="Clase"
                  >
                    <Select disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="servicio"
                    label="Servicio"
                  >
                    <Select disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_vehiculo"
                    label="Tipo de Vehículo"
                  >
                    <Select disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="linea"
                    label="Línea"
                  >
                    <Select disabled />
                  </Form.Item>
                </Col>
              </>
            )}
            {tipoDePadronSelected === padronesEnum.MOTOCICLETA && (
              <>
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Clave"
                      name="clave"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Número de Placa"
                      name="numero_de_placa_vigente"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      name="serie"
                      label="Serie"
                      tooltip="Número de Serie de la Motocicleta"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Registro Publico"
                      name="registro_publico_vehicular"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Tarjeta de Circulación"
                      name="tarjeta_de_circulacion"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>

                </Row>
              </>
            )}
            {tipoDePadronSelected === padronesEnum.REMOLQUE && (
              <>
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Clave"
                      name="clave"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Número de Placa"
                      name="numero_de_placa_vigente"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      name="serie"
                      label="Serie"
                      tooltip="Número de Serie de la Motocicleta"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Registro Publico"
                      name="registro_publico_vehicular"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={6}>
                    <Form.Item
                      label="Tarjeta de Circulación"
                      name="tarjeta_de_circulacion"
                    >
                      <Input allowClear disabled />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
        <Form.Item name="id" hidden>
          <Input disabled />
        </Form.Item>
      </Row>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  const onFinishDescuento = async (tipoDeDescuento, charges = [], cargosCompletos = []) => {
    setLoading(true);
    const { descripcion, comentarios } = tipoDeDescuento;
    const variables = tipoDeDescuento?.variables?.length ? tipoDeDescuento.variables : [];
    const values = {
      descripcion,
      tipo_de_descuento: tipoDeDescuento.id,
      variables,
      comentarios,
    };
    if (values.fecha_inicial) {
      const { fecha_inicial, fecha_final } = tipoDeDescuento;
      const diff = fecha_final.diff(fecha_inicial, 'days');
      values.importe = diff;
      delete tipoDeDescuento.fecha_inicial;
      delete tipoDeDescuento.fecha_final;
    }
    if (!values?.variables?.length) {
      delete values.variables;
    }
    values.object_id_padron = padron.id;
    values.content_type_padron = padron.tipo_de_padron;
    values.estado_del_descuento = 1;
    values.accionInf = 'DESCUENTOS';
    const {
      success,
      objectErrors,
      errorKeys,
    } = await postGenerarDescuentosEspeciales(values, charges, {
      clave: padron?.clave, idPadron: padron?.tipo_de_padron, cargosCompletos,
    });
    onCancelDescuento();
    if (success) {
      await getCargosPadron();
    }
    if (errorKeys.length) {
      Modal.warn({
        width: '70vw',
        style: { minWidth: 500 },
        okButtonProps: { style: { display: 'none', heigth: 0 } },
        bodyStyle: { paddingBottom: 0 },
        title: <Text strong>Errores al aplicar el descuento</Text>,
        content: (
          <>
            <Text strong>El descuento no se pudo aplicar a los siguientes cargos/recargos</Text>
            <br />
            <br />
            {errorKeys.map((e) => {
              const cargosIds = objectErrors[e];
              const _data = cargos.filter((c) => cargosIds.includes(c.id));
              return (
                <React.Fragment key={e}>
                  <b style={{ fontSize: 16, fontWeight: 'bolder' }}>
                    {descripcion}
                  </b>
                  {': '}
                  <br />
                  <span style={{ fontSize: 16, fontWeight: 'bolder' }}>
                    <span>
                      Razón:
                      {' '}
                      <span className="warning">
                        {e}
                      </span>
                    </span>
                  </span>
                  <br />
                  <br />
                  <List
                    pagination={{
                      position: 'bottom',
                      align: 'end',
                      pageSize: 5,
                      showQuickJumper: false,
                      showSizeChanger: false,
                      size: 'small',
                    }}
                    size="small"
                    dataSource={_data}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          title={`(${item.tipo_de_cargo.periodo_fiscal.periodo}) - ${item.descripcion}`}
                        />
                      </List.Item>
                    )}
                  />
                </React.Fragment>
              );
            })}

            <br />
            <Row justify="end" style={{ marginBottom: 20 }}>
              <Button
                // style={{ marginLeft: screens.md ? 10 : 0 }}
                onClick={() => Modal.destroyAll()}
                $accent
              >
                Entendido
              </Button>
            </Row>
          </>
        ),
      });
    }
    setLoading(false);
  };

  const getTitle = () => {
    if (padron) {
      return 'Padrón';
    }
    return 'Búsqueda del Padrón';
  };

  const asignarEntidad = async (user) => {
    setLoading(true);
    const { razon_social } = _entidad;
    const { entidad, nombre_completo, id } = user;
    entidad.push(entidad.id);
    const edited = await patchCiudadano(
      id,
      { entidad },
      `Ahora el usuario ${nombre_completo} también pertenece a ${razon_social}.`,
    );
    if (edited) {
      setPadron(setPadron(normalizePadron(edited)));
    }
    setLoading(false);
  };

  const msgAsignarEntidad = (user) => (
    message.info({
      content: (
        <>
          <Row style={{ width: '100%' }}>
            El contribuyente no pertenece a la entidad.
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              ¿Desea asignarlo a esta entidad?
            </strong>
          </Row>
          <Row align="middle" justify="center" style={{ width: '100%' }}>
            <Button
              type="link"
              onClick={() => asignarEntidad(user)}
            >
              Si
            </Button>
            <Button
              onClick={() => {
                message.destroy();
                form.resetFields();
              }}
              type="link"
            >
              No
            </Button>
          </Row>
        </>

      ),
      duration: 0,
    })
  );

  const onClickViewDiscounts = () => {
    const _descuentosPorAutorizar = cargos.map((e) => e.descuentosNoAplicadosArr).flat();
    setDescuentosPorAutorizar(_descuentosPorAutorizar);
    setVisibleAutorizarDescuentos(true);
  };

  const onClickCancelAppliedDiscounts = () => {
    const _descuentosACancelar = cargos.map((e) => e.descuentosAplicadosArr).flat();
    setDescuentosAplicados(_descuentosACancelar);
    setVisibleCancelarDescuentos(true);
  };

  const doesNotHavePermission = !periodoFiscalSelected?.vigente && !isAdmin();

  const getCargosFiltered = () => {
    const cargosIds = [...new Set(cargos.map((e) => e.id))];
    const cargosFiltered = cargosIds.map((id) => cargos.find((e) => e.id === id));
    return cargosFiltered;
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip={loadingMessage || 'Cargando...'} spinning={loading}>
        <Card
          className="form-container"
          title={getTitle()}
          bordered={false}
          extra={(
            <FormSubmitCustomControls
              loading={loading}
              controls={[
                {
                  text: 'Limpiar Búsqueda',
                  icon: <DeleteOutlined />,
                  onClick: onClear,
                },
              ]}
            />
          )}
        >
          <InfoPadron padron={padron} tipoDePadron={tipoDePadronSelected} />
          <FormBusqueda />
          <br />
          <Title level={4}>
            Descuentos Disponibles
          </Title>
          <Table
            cols={columns}
            data={descuentosDisponibles}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            allowAdd={false}
            allowEdit={false}
            allowDelete={false}
            allowSearch={false}
            allowSort={false}
            rowKey="id"
            customActions={[
              {
                text: 'Aplicar Descuento',
                icon: <PlusCircleOutlined />,
                onClick: () => setVisibleDescuentos(true),
                hasPermission: !doesNotHavePermission,
                disabled: restrict,
              },
              {
                text: 'Ver Descuentos por Revisar',
                icon: <InfoCircleOutlined />,
                onClick: onClickViewDiscounts,
                disabled: restrict,
              },
            ]}
            hasAllViewPermission={hasAllViewPermission}
            permission={permission}
          />
          <br />
          <Row justify="space-between">
            <Title level={4}>
              Cargos
            </Title>
            <Button
              type="link"
              className="danger"
              onClick={onClickCancelAppliedDiscounts}
              icon={<CloseCircleOutlined />}
              disabled={!cargos.length}
            >
              Cancelar Descuentos Aplicados
            </Button>
          </Row>
          <AntTable
            size="small"
            className="custom-table"
            rowKey="id"
            locale={{ emptyText: 'No hay datos' }}
            columns={columnsCargos}
            dataSource={getCargosFiltered()}
            scroll={{ y: 400 }}
            pagination={{ position: ['bottomLeft'] }}
            summary={() => {
              // Obtener datos filtrados una sola vez para optimización
              const allData = getCargosFiltered();

              // Campos a sumar en el resumen
              const camposTotales = [
                'importe',
                'adicionales',
                'recargo_total',
                'descuento_total_aplicado',
                'descuentos_por_autorizar',
                'actualizacion_total',
                'gasto_total',
                'importe_total',
              ];

              // Calcular todos los totales de una vez
              const totales = camposTotales.reduce((acc, campo) => {
                acc[campo] = allData.reduce((sum, registro) => sum + (registro?.[campo] || 0), 0);
                return acc;
              }, {});

              return (
                <AntTable.Summary.Row className="align-right bold">
                  {DEV && <AntTable.Summary.Cell />}
                  <AntTable.Summary.Cell />
                  <AntTable.Summary.Cell />
                  <AntTable.Summary.Cell>
                    Totales:
                  </AntTable.Summary.Cell>
                  {camposTotales.map((campo) => (
                    <AntTable.Summary.Cell key={campo} align="right">
                      {toCurrency(totales[campo])}
                    </AntTable.Summary.Cell>
                  ))}
                </AntTable.Summary.Row>
              );
            }}
          />
        </Card>
      </Spin>
      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaPadrones}
        setVisible={setVisibleBusquedaPadrones}
        callback={(pdrn) => setPadron(normalizePadron(pdrn))}
        dataExterno={dataExterno}
        setDataExterno={setDataExterno}
        tipoDePadron={tipoDePadronSelected}
        msgAsignarEntidad={msgAsignarEntidad}
      />
      <ModalAgregarDescuentos
        callback={onFinishDescuento}
        visible={visibleDescuentos}
        setVisible={setVisibleDescuentos}
        tipoDePadron={tipoDePadronSelected}
        cargos={cargos}
        tipoDeDescuento={selectedRowKeys[0]}
      />
      <ModalAutorizarDescuentosEspeciales
        callback={() => {
          setDescuentosPorAutorizar([]);
          getCargosPadron();
        }}
        visible={visibleAutorizarDescuentos}
        setVisible={setVisibleAutorizarDescuentos}
        descuentosPorAutorizar={descuentosPorAutorizar}
      />
      <ModalCancelarDescuentosAplicados
        callback={() => {
          setVisibleCancelarDescuentos(false);
          setDescuentosPorAutorizar([]);
          getCargosPadron();
        }}
        visible={visibleCancelarDescuentos}
        setVisible={setVisibleCancelarDescuentos}
        descuentosAplicados={descuentosAplicados}
      />
      <ModalAlertas padron={padron} setRestrict={setRestrict} />
    </Row>
  );
};

export default DescuentosEspeciales;
